/**
 * HelpMenu — post-completion help mode.
 *
 * It used to offer every completed person exactly two buttons:
 *
 *   🏪 Replay Shop Setup        -> replayTutorial('manager')
 *   📋 Replay Daily Operations  -> replayTutorial('user')
 *
 * Neither is necessarily theirs. A technician who finished
 * `technician_core_v1` was offered two unrelated walkthroughs, and taking
 * either wrote a durable row under a contract they had never been resolved
 * to — the manager library, route-filtered down to whatever a technician can
 * open, persisted under the `manager` key. One key is supposed to mean one
 * exact ordered sequence.
 *
 * There is now one replay: this person's own experience, named from the single
 * label authority so the button says what they will actually get.
 */

import { HelpCircle } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

interface HelpMenuProps {
  /** Replays this person's resolved experience. Takes no flow, by design. */
  onReplay: () => void;
  onDismiss: () => void;
  /** What that experience is called, e.g. "Working a ticket". */
  experienceLabel: string | null;
}

export const HelpMenu = ({
  onReplay,
  onDismiss,
  experienceLabel,
}: HelpMenuProps) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="fixed bottom-4 right-4 z-[10000] mb-safe-bottom flex items-center justify-center w-14 h-14 sm:w-12 sm:h-12 bg-primary text-primary-foreground rounded-full shadow-lg hover:scale-110 transition-transform"
          data-testid="tutorial-help-button"
          data-floating-control="help"
          data-yield-to-modal=""
          aria-label="Tutorial help"
        >
          <HelpCircle className="h-6 w-6 sm:h-5 sm:w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end" side="top">
        <div className="space-y-2">
          <div className="text-sm font-semibold text-foreground mb-3">
            Walkthrough
          </div>
          {experienceLabel && (
            <button
              data-testid="help-menu-replay"
              onClick={() => onReplay()}
              className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors"
            >
              ↻ Replay “{experienceLabel}”
            </button>
          )}
          <Separator className="my-2" />
          <button
            data-testid="help-menu-hide"
            onClick={onDismiss}
            className="w-full text-left px-3 py-2 text-sm text-muted-foreground rounded-md hover:bg-accent transition-colors"
          >
            Hide Help Button
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
